import { dayjs } from '@/lib/dayjs';
import { useState, useEffect, useContext, useRef } from 'react';
import Typography from '@mui/material/Typography';
import { fetchExprData, fetchTooltip, extractTrackedParams, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import DataContext from '@/contexts/DataContext';
import { useComponentLogger } from '@/lib/component-logger';

import Box from '@mui/material/Box';
// import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { InfoTooltip } from '../typography/info-tooltip';
import { NoSsr } from '@/components/core/no-ssr';
import Paper from '@mui/material/Paper';
import { getDefaultColor } from '@/lib/dataHandlers/ChartHelpers';
import { chartColors, textColors } from '@/styles/theme/colors';

export default function MyBar({ config }) {
  const logger = useComponentLogger("Bar Chart");
  const chartHeight = 300;

  const { currentProject, selectedViewParams, changedParams } = useContext(DataContext);
  const [barData, setBarData] = useState([]);
  const [dataKeys, setDataKeys] = useState([]);
  const [tooltip, setTooltip] = useState(null);
  const [title, setTitle] = useState(config.title || '');
  const [unit, setUnit] = useState('');
  const [barColor, setBarColor] = useState('var(--mui-palette-primary-main)');
  const trackedParamValsRef = useRef({});
  const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr

  const fetchContent = async () => {
    // logger.debug("FetchContent executing.................")
    if (config?.dataSource === null || config?.dataSource === undefined) return;

    const dataSource = config.dataSource;
    
    if (dataSource.type === "text") {
      setBarData(dataSource.text);
    } else if (dataSource.type === "apiExpr") {  
      const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);
      logger.debug('bar data', data)
      
      if (data?.rows && data.rows.length > 0 && data?.cols && data.cols.length > 0) {
        let chartDataArr = [];
        let keys = new Set();
        let dataUnit = '';

        data.rows.forEach(row => {
          let chartDataPoint = {};
          data.cols.forEach(col => {
            if (col.name === 'ts') {
              const date = dayjs(row[col.name]?.val || row[col.name]);
              chartDataPoint['name'] = date.toISOString();
            } else {
              const value = row[col.name]?.val;
              if (value !== undefined) {
                const displayName = col?.meta?.dis || col?.meta?.navName || col.meta?.id?.dis || col.name;
                chartDataPoint[displayName] = value;
                keys.add(displayName);
                
                // Extract unit from the first non-timestamp column
                if (!dataUnit && row[col.name]?.unit) {
                  dataUnit = row[col.name].unit;
                }
              }
            }
            // logger.debug('bar col meta', col.meta);

            // Assign bar colours
            let color = '';
            if (col?.meta?.color) {
              // have to access this key ('fill') to modify the colour in recharts
              color = chartColors[col.meta.color] || col.meta.color;
            } else {
              const colorIndex = 0; // want all the bars to stay the same colour, so keep the index constant
              let colorData = getDefaultColor(colorIndex); 
              color = colorData.hex;
            }
            chartDataPoint['fill'] = color;
            setBarColor(color);
          });

          if (Object.keys(chartDataPoint).length > 1) {
            chartDataArr.push(chartDataPoint);
          }
        });

        setBarData(chartDataArr);
        setDataKeys(Array.from(keys));
        setUnit(dataUnit);

        // logger.debug('bar data', chartDataArr)
      }
    }
  };

  const processTooltip = async () => {
    let tooltipContent = await fetchTooltip(currentProject, selectedViewParams, config);
    if (tooltipContent) setTooltip(tooltipContent);
  };

      
  // useEffect(() => {
  //   fetchContent().catch(() => {});
  //   processTooltip().catch(() => {});
  // }, [selectedViewParams]);

  useEffect(() => {
      let trackedParams = extractTrackedParams(config.dataSource);
      let params = {};
      for (const param of trackedParams) {
          if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
      }

      logger.debug('tracked params', params);
      trackedParamValsRef.current = params;
      trackedParamNamesRef.current = trackedParams;

      fetchContent().catch(() => {});
      processTooltip().catch(() => {});
  }, []);


    useEffect(() => {
      let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
      logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch);
      if (shouldReFetch.changed) {
          trackedParamValsRef.current = shouldReFetch.paramRef;
          fetchContent();
          processTooltip();
      }

      // make sure to clear the content if any of the tracked params were removed
      // common use case: when a parent param changes, it clears the child param
      for (const param of trackedParamNamesRef.current) { 
          if (!selectedViewParams[param]) {
              setBarData([]);
              return;
          }
      }
    }, [changedParams]);

  const getAllTimestamps = (data) => {
    return data.map(item => new Date(item.name).getTime());
  };

  const calculateDaysDifference = (start, end) => {
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const earliestTimestamp = new Date(Math.min(...getAllTimestamps(barData)));
  const latestTimestamp = new Date(Math.max(...getAllTimestamps(barData)));

  const daysSpan = calculateDaysDifference(earliestTimestamp, latestTimestamp);

  const isShortSpan = daysSpan <= 7;

  const formatXAxisTick = (tick) => {
    return isShortSpan ? dayjs(tick).format('MM/DD HH:mm') : dayjs(tick).format('YY/MM/DD');
  };

  // Format the legend text
  // https://recharts.org/en-US/api/Legend#formatter
  const renderLegend = (value, entry) => {
    // const { color } = textColors['black'];

    // return <span style={{ color: 'text.secondary' }}>{value}</span>;
    // change to typography instead of span bc original text was cut-off on mobile and looks more consistent with dynamic charts
    // return  <Typography color="text.secondary" variant="body2" display={'inline'}>{value}</Typography>
    return  <Typography variant="body2" display={'inline'}>{value}</Typography>
  };

  return (
    <Stack spacing={2}>
      {title && (
        <Typography color="text.secondary" variant="h6" sx={{display: 'flex', alignItems: 'center'}}>
          {title}
          {tooltip && <InfoTooltip content={tooltip} />}
        </Typography> 
      )}
      <NoSsr fallback={<Box sx={{ height: `${chartHeight}px` }} />}>
        <ResponsiveContainer height={chartHeight}>
          {/* <BarChart data={barData} margin={{ top: 0, right: 20, bottom: 40, left: 20 }}> */}
          <BarChart data={barData} margin={{ top: 0, right: 20, bottom: 40, left: 5 }} layout='horizontal'>
            <CartesianGrid strokeDasharray="2 4" vertical={false} />
            <XAxis 
              axisLine={false} 
              dataKey="name" 
              tickLine={false} 
              type="category" 
              angle={-45} 
              tickFormatter={formatXAxisTick} 
              textAnchor='end' 
            />
            <YAxis 
              label={{ value: unit, angle: -90, position: 'insideLeft' }} 
            />
            {dataKeys.map(key => (
              <Bar
                key={key}
                animationDuration={300}
                barSize={24}
                dataKey={key}
                fill = {barColor}
                name={key}
              />
            ))}
            {/* <Legend verticalAlign='top' height={36} formatter={renderLegend} layout='vertical'/> */}
            <Legend verticalAlign='top' formatter={renderLegend} layout='vertical' wrapperStyle={{ paddingBottom: '1rem'}}/> 
            {/* <Legend verticalAlign='bottom' formatter={renderLegend} layout='vertical' wrapperStyle={{ marginTop: '10rem'}}/> */}
            <Tooltip animationDuration={50} content={<TooltipContent />} cursor={false} />
          </BarChart>
        </ResponsiveContainer>
      </NoSsr>
    </Stack>
  );
}

function TooltipContent({ active, payload, label }) {
  if (!active) {
    return null;
  }

  return (
    <Paper sx={{ border: '1px solid var(--mui-palette-divider)', boxShadow: 'var(--mui-shadows-16)', p: 1 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">{dayjs(label).format('YYYY-MM-DD HH:mm')}</Typography>
        {payload?.map((entry) => (
          <Stack direction="row" key={entry.name} spacing={2} sx={{ alignItems: 'center' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: '1 1 auto' }}>
              <Box sx={{ bgcolor: entry.fill, borderRadius: '2px', height: '8px', width: '8px' }} />
              <Typography sx={{ whiteSpace: 'nowrap' }}>{entry.name}</Typography>
            </Stack>
            <Typography color="text.secondary" variant="body2">
              {formatValue(entry.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

function formatValue(value) {
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2, 
    }).format(value);
  }
  return value;
}
